import React, { useEffect, useState, useMemo } from "react";
import { useUserPage, useSaveUser } from "../../../api/queries/adminQueries";
import {
  useRoles,
  useCompanies,
  useAllBranches,
  useDepartments,
} from "../../../api/queries/organizationQueries";
import LinkableEmployeePicker from "./LinkableEmployeePicker";
import TemporaryPasswordDialog from "./TemporaryPasswordDialog";
import { credentialsNotice } from "./userCredentials";
import { getApiErrorMessage } from "../../../utils/apiError";
import Layout from "../../../components/layout/Layout";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import usePermissions from "../../../hooks/usePermissions";
import { useCanModify } from "../../../hooks/usePermissions";

const Users = () => {
  // View-only roles see the list without create / edit / status actions
  const canModify = useCanModify("users", { company: true });
  // Only the super admin may assign the Super Admin role
  const isSuperAdmin = usePermissions().superAdmin;
  const localCompanyId = localStorage.getItem("companyId") || "";

  const initialFormState = {
    id: "",
    name: "",
    email: "",
    password: "",
    sendCredentials: true,
    sendCredentialsTo: "",
    roleId: "",
    employeeId: "",
    companyId: localCompanyId,
    branchId: "",
    assignmentType: "both",
    departmentId: "",
    isActive: true,
  };

  /* ===================== LOCAL STATE ===================== */
  const [formData, setFormData] = useState(initialFormState);

  // Server data. Branches/departments follow the company selected in the form.
  const { data: roles = [] } = useRoles();
  // Employee linked to the account (picker value; null = no link)
  const [linkedEmployee, setLinkedEmployee] = useState(null);
  const { data: companies = [] } = useCompanies();
  // Branch options: the account's company and its parent companies (the
  // same rule as a branch's parent); the employee must then be in that branch
  const { data: everyBranch = [] } = useAllBranches(true);
  const companyChain = useMemo(() => {
    const ids = new Set();
    for (let cur = formData.companyId; cur && !ids.has(cur);) {
      ids.add(cur);
      cur = companies.find((c) => c.id === cur)?.parentId || null;
    }
    return ids;
  }, [formData.companyId, companies]);
  const branches = useMemo(
    () =>
      (Array.isArray(everyBranch) ? everyBranch : [])
        .filter((b) =>
          companyChain.has(b.companyId || b.companyID || b.company?.id),
        )
        .sort((a, b) => (a.branchName || "").localeCompare(b.branchName || "")),
    [everyBranch, companyChain],
  );
  // Is branch `id` the branch `areaId` or under it? (unknown branch: yes,
  // the backend decides on save)
  const isBranchUnder = (id, areaId) => {
    const list = Array.isArray(everyBranch) ? everyBranch : [];
    if (!list.some((b) => b.id === id)) return true;
    const seen = new Set();
    for (let cur = id; cur && !seen.has(cur);) {
      if (cur === areaId) return true;
      seen.add(cur);
      cur = list.find((b) => b.id === cur)?.parentId || null;
    }
    return false;
  };
  const companyNameOf = (id) => companies.find((c) => c.id === id)?.companyName;
  const { data: departments = [] } = useDepartments(
    formData.companyId || localCompanyId,
  );
  const saveUser = useSaveUser();
  const isSaving = saveUser.isPending;
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // A generated password that could not be emailed: { email, password }, shown once
  const [temporary, setTemporary] = useState(null);

  /* ===================== FILTER & PAGINATION STATE ===================== */
  // Search, role, status and paging run on the server; sorting applies to the current page
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "email",
    direction: "asc",
  });

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, roleFilter]);

  const usersQuery = useUserPage({
    search: search || undefined,
    status: statusFilter === "All" ? undefined : statusFilter.toLowerCase(),
    roleId: roleFilter === "All" ? undefined : roleFilter,
    page: currentPage,
    limit: itemsPerPage,
  });
  const pageRows = usersQuery.data?.data;

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="bi bi-arrow-down-up ms-2 opacity-25 fs-060"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-sort-down ms-2 text-primary fs-070"></i>
    ) : (
      <i className="bi bi-sort-up ms-2 text-primary fs-070"></i>
    );
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setFormData({ ...initialFormState, companyId: localCompanyId });
    setLinkedEmployee(null);
    setShowModal(true);
  };

  const handleOpenEdit = (user) => {
    setIsEditMode(true);

    // Branches/departments load for this company via formData.companyId
    const targetCompanyId =
      user.companyId || user.company?.id || localCompanyId;

    setFormData({
      id: user.id,
      name: user.employeeFirstName
        ? `${user.employeeFirstName} ${user.employeeLastName}`
        : "",
      email: user.email || "",
      password: "",
      roleId: user.roleId || "",
      employeeId: user.employeeId || user.employee?.id || "",
      companyId: targetCompanyId,
      branchId: user.branchId || user.branch?.id || "",

      departmentId: user.departmentId || "", // Map ID directly
      isActive: user.isActive,
    });
    const empId = user.employeeId || user.employee?.id;
    const first = user.employee?.firstName || user.employeeFirstName || "";
    const last = user.employee?.lastName || user.employeeLastName || "";
    setLinkedEmployee(
      empId
        ? {
            id: empId,
            name: `${first} ${last}`.trim(),
            employeeNumber: user.employee?.employeeId || "",
            branchId: user.employee?.branchId ?? undefined,
          }
        : null,
    );
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Mapped exactly to your required JSON payload
    const payload = {
      email: formData.email,
      password: formData.password?.trim() ? formData.password : undefined,
      companyId: formData.companyId,
      branchId: formData.branchId || null,
      employeeId: formData.employeeId || null,
      roleId: formData.roleId,
      departmentId: formData.departmentId || null, // Corrected to use ID
      isActive: formData.isActive,
      isEmailVerified: false,
    };

    const sendTo = formData.sendCredentialsTo?.trim();
    if (isEditMode) payload.id = formData.id;
    else {
      payload.sendCredentials = formData.sendCredentials;
      if (formData.sendCredentials && sendTo)
        payload.sendCredentialsTo = sendTo;
    }

    try {
      const saved = await saveUser.mutateAsync(payload);
      setShowModal(false);
      if (isEditMode) {
        toast.success("User saved successfully!");
        return;
      }
      const notice = credentialsNotice(saved, sendTo || formData.email);
      toast[notice.tone](notice.text);
      if (saved?.temporaryPassword) {
        setTemporary({
          email: formData.email,
          password: saved.temporaryPassword,
        });
      }
      saveUser.reset(); // don't keep the response (and its password) around
    } catch (err) {
      toast.error(getApiErrorMessage(err, "An error occurred."));
    }
  };

  const handleStatusToggle = (user) => {
    if (user.isActive) {
      setPendingUser(user);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(user);
    }
  };

  const executeStatusUpdate = async (user) => {
    try {
      await saveUser.mutateAsync({ id: user.id, isActive: !user.isActive });
      toast.success("Status updated!");
      setShowConfirm(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update status."));
    }
  };

  /* ===================== SORT (current page) & PAGINATION ===================== */
  const currentData = useMemo(
    () =>
      [...(pageRows || [])].sort((a, b) => {
        const valA = String(a[sortConfig.key] || "").toLowerCase();
        const valB = String(b[sortConfig.key] || "").toLowerCase();
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }),
    [pageRows, sortConfig],
  );
  const totalItems = usersQuery.data?.total || 0;

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">
              User Management
            </h1>
            <p className="text-muted small mb-0">
              Control system access, roles, and deployment types
            </p>
          </div>
          {canModify && (
            <button
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={handleOpenAdd}
            >
              <i className="bi bi-plus-lg me-2"></i>Add New
            </button>
          )}
        </div>

        <div className="row g-2 mb-4">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control filter-input"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select filter-input"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="All">All Roles</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select filter-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header sortable">
                <tr>
                  {[
                    { label: "USER / EMPLOYEE", key: "email" },
                    { label: "EMAIL", key: "email" },
                    { label: "BRANCH", key: "branchName" },
                    { label: "STATUS", key: "isActive" },
                    { label: "ACTIONS", key: null },
                  ].map((col, idx) => (
                    <th
                      key={idx}
                      className={`${idx === 0 ? "ps-4" : ""} py-3 border-0 ${col.key ? "cursor-pointer" : "cursor-default"}`}
                      onClick={() => col.key && requestSort(col.key)}
                    >
                      {col.label} {col.key && getSortIcon(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="border-top-0">
                {currentData.map((user) => {
                  // The super admin has no company; it can't be changed or deactivated here
                  const superAdmin =
                    (user.companyId === null && !user.company) ||
                    /^super\s*admin$/i.test(
                      roles.find((r) => r.id === user.roleId)?.title ||
                        user.role?.title ||
                        "",
                    );
                  return (
                    <tr key={user.id} className="align-middle">
                      <td className="ps-4 py-3">
                        {user.employee || user.employeeFirstName ? (
                          <div>
                            <div className="fw-bold text-dark fs-085">
                              {user.employee?.firstName ||
                                user.employeeFirstName}{" "}
                              {user.employee?.lastName || user.employeeLastName}
                            </div>
                            <div className="text-muted small fs-070">
                              {roles.find((r) => r.id === user.roleId)?.title ||
                                "User"}
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted italic small">
                            System Account (Unlinked)
                          </div>
                        )}
                      </td>
                      <td className="fs-080 text-slate-700">
                        {user.email}
                        {superAdmin && (
                          <span className="status-pill status-pill--blue ms-2">
                            <i className="bi bi-shield-lock me-1"></i>Super
                            Admin
                          </span>
                        )}
                      </td>
                      <td className="fs-080 text-slate-700">
                        {user.branch?.branchName ||
                          user.branchName ||
                          "Main HQ"}
                      </td>
                      <td>
                        <span
                          className={`status-pill status-pill--${user.isActive ? "green" : "gray"}`}
                        >
                          {toCamelCase(user.isActive ? "Active" : "Inactive")}
                        </span>
                      </td>
                      <td className="pe-4 text-end">
                        {canModify ? (
                          <>
                            {superAdmin ? (
                              <span
                                className="fs-070 text-slate-400"
                                title="The super admin account is managed on the server"
                              >
                                Protected
                              </span>
                            ) : (
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  className="btn btn-sm btn-outline-primary px-3 fw-bold rounded-6 fs-075"
                                  onClick={() => handleOpenEdit(user)}
                                >
                                  Edit
                                </button>
                                <button
                                  className={`btn btn-sm ${user.isActive ? "btn-outline-danger" : "btn-outline-success"} px-3 fw-bold rounded-6 fs-075`}
                                  onClick={() => handleStatusToggle(user)}
                                >
                                  {user.isActive ? "Deactivate" : "Activate"}
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="fs-070 text-slate-400">
                            View only
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={currentPage}
            limit={itemsPerPage}
            total={totalItems}
            onPageChange={setCurrentPage}
            onLimitChange={(v) => {
              setItemsPerPage(v);
              setCurrentPage(1);
            }}
            pageSizes={[10, 25, 50]}
          />
        </div>
      </div>

      {showConfirm && (
        <Dialog
          title="Confirm Deactivation"
          onClose={() => setShowConfirm(false)}
          size="sm"
        >
          <div className="text-start">
            <p className="text-muted small">
              Are you sure you want to deactivate{" "}
              <strong>{pendingUser?.email}</strong>?
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger profile-btn"
                onClick={() => executeStatusUpdate(pendingUser)}
              >
                Yes, Deactivate
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {showModal && (
        <Dialog
          title={<>{isEditMode ? "Edit" : "Create"} User Account</>}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSave}>
            <div className="row g-3">
              <div className="col-md-12">
                <label className="profile-label">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="user-password" className="profile-label">
                  {isEditMode
                    ? "Password (Leave blank to keep)"
                    : "Temporary password (leave empty to generate)"}
                </label>
                <input
                  id="user-password"
                  type="password"
                  className="form-control"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="profile-label">System Role</label>
                <select
                  className="form-select"
                  value={formData.roleId}
                  onChange={(e) =>
                    setFormData({ ...formData, roleId: e.target.value })
                  }
                  required
                >
                  <option value="">Select Role</option>
                  {roles
                    .filter(
                      (r) =>
                        isSuperAdmin || !/^super\s*admin$/i.test(r.title || ""),
                    )
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.title}
                      </option>
                    ))}
                </select>
              </div>
              {!isEditMode && (
                <div className="col-12">
                  <div className="form-check">
                    <input
                      id="user-send-credentials"
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.sendCredentials}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          sendCredentials: e.target.checked,
                        })
                      }
                    />
                    <label
                      htmlFor="user-send-credentials"
                      className="form-check-label fs-085"
                    >
                      Email the login and temporary password
                    </label>
                  </div>
                  {formData.sendCredentials && (
                    <div className="mt-2">
                      <label htmlFor="user-send-to" className="profile-label">
                        Send credentials to
                      </label>
                      <input
                        id="user-send-to"
                        type="email"
                        className="form-control"
                        value={formData.sendCredentialsTo}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            sendCredentialsTo: e.target.value,
                          })
                        }
                      />
                      <div className="fs-070 text-slate-500 mt-1">
                        Leave empty to use the login email.
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="col-md-6">
                <label htmlFor="user-company" className="profile-label">
                  Assigning Company
                </label>
                <select
                  id="user-company"
                  className="form-select"
                  value={formData.companyId}
                  onChange={(e) => {
                    // A new company: pick the branch and employee again
                    setFormData({
                      ...formData,
                      companyId: e.target.value,
                      branchId: "",
                      employeeId: "",
                    });
                    setLinkedEmployee(null);
                  }}
                  required
                >
                  <option value="">Select Company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.companyName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label htmlFor="user-branch" className="profile-label">
                  Assign to Branch
                </label>
                <select
                  id="user-branch"
                  className="form-select"
                  value={formData.branchId}
                  disabled={!formData.companyId}
                  onChange={(e) => {
                    const branchId = e.target.value;
                    // The linked employee must work in the account's branch
                    // or under it (the branch is the area the account covers)
                    const keep =
                      !linkedEmployee ||
                      !branchId ||
                      linkedEmployee.branchId === undefined ||
                      isBranchUnder(linkedEmployee.branchId, branchId);
                    setFormData({
                      ...formData,
                      branchId,
                      employeeId: keep ? formData.employeeId : "",
                    });
                    if (!keep) setLinkedEmployee(null);
                  }}
                >
                  <option value="">None (Main HQ)</option>
                  {formData.branchId &&
                    !branches.some((b) => b.id === formData.branchId) && (
                      <option value={formData.branchId}>Current branch</option>
                    )}
                  {branches.map((b) => {
                    const owner = b.companyId || b.companyID || b.company?.id;
                    return (
                      <option key={b.id} value={b.id}>
                        {b.branchName}
                        {owner !== formData.companyId && companyNameOf(owner)
                          ? ` (${companyNameOf(owner)})`
                          : ""}
                      </option>
                    );
                  })}
                </select>
                <div className="fs-070 text-slate-500 mt-1">
                  The area this account covers (its sub-branches included).
                </div>
              </div>
              <div className="col-md-12">
                <label htmlFor="user-employee" className="profile-label">
                  Link to Employee
                </label>
                <LinkableEmployeePicker
                  id="user-employee"
                  companyId={formData.companyId}
                  branchId={formData.branchId}
                  userId={isEditMode ? formData.id : undefined}
                  value={linkedEmployee}
                  onChange={(emp) => {
                    setLinkedEmployee(emp);
                    setFormData({
                      ...formData,
                      employeeId: emp?.id || "",
                      // No branch yet: take the employee's branch
                      branchId:
                        emp && !formData.branchId && emp.branchId
                          ? emp.branchId
                          : formData.branchId,
                    });
                  }}
                />
                <div className="fs-070 text-slate-500 mt-1">
                  Only employees of the selected company
                  {formData.branchId
                    ? " and branch (or a branch under it)"
                    : ""}{" "}
                  who have no account yet.
                </div>
              </div>
              <div className="col-12">
                <label className="profile-label">Department</label>
                <select
                  className="form-select"
                  value={formData.departmentId}
                  onChange={(e) =>
                    setFormData({ ...formData, departmentId: e.target.value })
                  }
                >
                  <option value="">Select Dept</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.departmentName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <label className="form-check-label fs-085">
                    Active Status
                  </label>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary profile-btn"
                disabled={isSaving}
              >
                {isSaving ? "Processing..." : "Confirm & Save"}
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {temporary && (
        <TemporaryPasswordDialog
          email={temporary.email}
          password={temporary.password}
          onClose={() => setTemporary(null)}
        />
      )}
    </Layout>
  );
};

export default Users;
